<template>
  <div>
    <v-dialog v-model="dialog" persistent scrollable max-width="560px">
      <v-card class="profile-dialog-card entity-form-dialog classroom-report-dialog">
        <div class="profile-dialog-header entity-form-dialog__header">
          <div class="entity-form-dialog__title">
            <v-avatar size="44" color="green lighten-5">
              <v-icon color="primary">mdi-file-chart-outline</v-icon>
            </v-avatar>
            <div>
              <span>{{ sourceLabel }} report cards</span>
              <h2>Create {{ sourceLabel.toLowerCase() }} report cards</h2>
              <p>{{ classroomName }}</p>
            </div>
          </div>
          <v-btn icon aria-label="Close classroom report card dialog" @click.stop="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-text class="profile-dialog-body entity-form-dialog__body">
          <section class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading">
              <span><v-icon size="19" color="primary">mdi-calendar-range</v-icon></span>
              <div>
                <h3>Academic year</h3>
                <p>Generate one combined PDF for every student in this classroom.</p>
              </div>
            </div>

            <v-select
              v-model="selectedYear"
              :items="years"
              :loading="yearsLoading"
              :disabled="yearsLoading"
              item-text="name"
              return-object
              outlined
              dense
              hide-details
              label="Select academic year"
              prepend-inner-icon="mdi-calendar-outline"
              no-data-text="No academic years available"
            />

            <div v-if="!yearsLoading && !years.length" class="classroom-report-dialog__empty">
              No academic years are configured for this classroom’s department.
            </div>
          </section>

          <section class="classroom-report-dialog__regenerate">
            <div class="classroom-report-dialog__regenerate-icon">
              <v-icon size="20" color="warning darken-2">mdi-refresh-alert</v-icon>
            </div>
            <div>
              <strong>Force regeneration</strong>
              <p>Rebuild saved report cards using the latest grades and comments. Leave this off to reuse existing reports.</p>
            </div>
            <v-switch
              v-model="forceRegenerate"
              inset
              hide-details
              aria-label="Force regenerate report cards"
            />
          </section>
        </v-card-text>

        <v-divider />

        <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
          <v-btn text @click="close">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            :disabled="!selectedYear"
            :loading="loading"
            @click="generate"
          >
            <v-icon left small>mdi-file-refresh-outline</v-icon>
            {{ forceRegenerate ? "Regenerate report cards" : "Create report cards" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showPdf"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      content-class="report-preview-dialog"
    >
      <v-card class="report-preview">
        <header class="report-preview__header">
          <div class="report-preview__identity">
            <span class="report-preview__icon">
              <v-icon color="primary">mdi-file-pdf-box</v-icon>
            </span>
            <div class="report-preview__heading">
              <span>Report cards</span>
              <h2>Classroom report cards</h2>
              <p>{{ reportPreviewContext }}</p>
            </div>
          </div>
          <v-btn
            icon
            class="report-preview__close"
            aria-label="Close classroom report card preview"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>
        <main class="report-preview__document">
          <embed
            :src="pdf"
            type="application/pdf"
            title="Classroom report cards PDF"
          />
        </main>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";
import yearService from "@/modules/ReportCard/Year/service";
import classRoomService from "@/modules/ClassRoom/service";
import virtualClassRoomService from "@/modules/VirtualClassRoom/service";

pdfMake.vfs = pdfFonts.vfs;

export default {
  name: "classroom-report-card",
  props: {
    ClassRoom: Object,
    source: { type: String, default: "regular" },
  },

  data() {
    return {
      dialog: true,
      loading: false,
      yearsLoading: false,
      showPdf: false,
      pdf: null,
      years: [],
      selectedYear: null,
      forceRegenerate: false,
    };
  },

  mounted() {
    this.getYears();
  },

  beforeDestroy() {
    this.clearPdfUrl();
  },

  computed: {
    sourceLabel() {
      return this.source === "virtual" ? "Virtual classroom" : "Classroom";
    },
    classroomName() {
      if (!this.ClassRoom) return "Selected classroom";
      const department = this.ClassRoom.Class && this.ClassRoom.Class.name;
      return [this.ClassRoom.name, department].filter(Boolean).join(" · ") || "Selected classroom";
    },
    reportPreviewContext() {
      const year = this.selectedYear && this.selectedYear.name;
      return [this.classroomName, year].filter(Boolean).join(" · ");
    },
  },

  methods: {
    clearPdfUrl() {
      if (this.pdf) {
        URL.revokeObjectURL(this.pdf);
        this.pdf = null;
      }
    },

    close() {
      this.clearPdfUrl();
      this.dialog = false;
      this.showPdf = false;
      this.$emit("close");
    },

    async getYears() {
      this.yearsLoading = true;
      try {
        const res = await yearService.getAll({
          filteredClasses: [this.ClassRoom.ClassId],
        });
        this.years = res.data || [];
      } catch (error) {
        console.log("error getting years", error);
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title:
              (error &&
                error.response &&
                error.response.data &&
                error.response.data.message) ||
              error.message ||
              "Unable to load years",
          });
      } finally {
        this.yearsLoading = false;
      }
    },

    async generate() {
      try {
        this.loading = true;

        const service = this.source === "virtual"
          ? virtualClassRoomService
          : classRoomService;
        const res = await service.generateClassroomReportCard({
          [this.source === "virtual" ? "VirtualClassRoomId" : "ClassRoomId"]: this.ClassRoom.id,
          YearId: this.selectedYear.id,
          forceRegenerate: this.forceRegenerate,
        });

        const docDefinition =
          res?.data?.docDefinition ||
          res?.docDefinition ||
          res?.data?.data?.docDefinition ||
          null;

        if (!docDefinition) {
          throw new Error("No report definition returned from server");
        }

        this.clearPdfUrl();

        pdfMake.createPdf(docDefinition).getBlob((blob) => {
          this.pdf = URL.createObjectURL(blob);
          this.showPdf = true;
          this.dialog = false;
          this.loading = false;
        });
      } catch (error) {
        console.log("generate report card error", error);
        this.loading = false;

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title:
              error?.response?.data?.message ||
              error?.message ||
              "Unable to generate report card",
          });
      }
    },
  },
};
</script>

<style scoped>
.classroom-report-dialog__regenerate {
  display: flex;
  gap: 12px;
  padding: 14px;
  align-items: center;
  border: 1px solid #f0dfb9;
  border-radius: 12px;
  background: #fffbf1;
}

.classroom-report-dialog__regenerate-icon {
  display: flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #fff4d8;
}

.classroom-report-dialog__regenerate > div:nth-child(2) {
  min-width: 0;
  flex: 1;
}

.classroom-report-dialog__regenerate strong {
  color: #5e512f;
  font-size: 12px;
}

.classroom-report-dialog__regenerate p {
  margin: 2px 0 0;
  color: #8a7a52;
  font-size: 10px;
  line-height: 1.45;
}

.classroom-report-dialog__regenerate ::v-deep .v-input--switch {
  flex: 0 0 auto;
  margin: 0;
}

.classroom-report-dialog__empty {
  margin-top: 12px;
  padding: 11px;
  border: 1px dashed #dce4dd;
  border-radius: 9px;
  color: #879189;
  background: #fafcfb;
  font-size: 11px;
  text-align: center;
}

@media (max-width: 599px) {
  .classroom-report-dialog__regenerate {
    align-items: flex-start;
  }

  .classroom-report-dialog__regenerate-icon {
    display: none;
  }
}
</style>
